/**
 * Handoff room token fetch — the ONLY provider seam in apps/platform.
 *
 * Master prompt §6 (the One Rule): the app NEVER holds a provider key. After a
 * PERMITTED handoff (the call has reached `handoff_requested` / `human_active`
 * and the staff member holds `handoff:handle`), the app asks the BACKEND for a
 * short-lived, opaque LiveKit room token and passes it — and only it — to the
 * media plane via `useHandoffRoom`. The token is treated as opaque: the app
 * never parses it, never persists it, and never caches it in the query cache.
 *
 * BACKEND GAP (documented seam): the architecture
 * (03-hostesa-app/06-livekit-browser-voice.md) specifies a dedicated endpoint
 *   POST /api/v1/livekit/token  Body: { callId }  ->  { token, serverUrl }
 * which does NOT exist in the live backend OpenAPI snapshot
 * (packages/api-client/openapi.json, 86 paths — no `/livekit/token`). The
 * existing POST .../calls/:callId/handoff returns a handoff STATUS
 * ({ callId, status }), NOT a room token, so it cannot be repurposed. Until the
 * backend ships the token endpoint, {@link fetchHandoffRoomToken} returns a
 * typed `unavailable` result and the UI shows a calm "not ready" state. When the
 * endpoint lands, swap the body of {@link fetchHandoffRoomToken} for the real
 * POST — no other code in this module changes.
 */

import { type ApiClient, ApiError, useApiClient } from '@hostesa/api-client';
import type { HandoffToken } from '@hostesa/livekit-client';
import { useEffect, useRef, useState } from 'react';

/**
 * Reason a token could not be issued. Mirrors the room state machine so the
 * panel can render one calm, text+icon state per cause (never color-only).
 */
export type HandoffTokenErrorReason =
  /** Backend rejected the request: not authorised, call not in handoff state. */
  | 'token_denied'
  /** The backend token endpoint does not exist yet (documented gap above). */
  | 'unavailable'
  /** Network / unexpected failure reaching the backend. */
  | 'failed';

export type HandoffTokenResult =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly token: HandoffToken }
  | { readonly status: 'error'; readonly reason: HandoffTokenErrorReason };

/**
 * Request a short-lived LiveKit room token from the backend for `callId`.
 *
 * SEAM: the one place the app crosses into provider territory, and even here it
 * only receives an OPAQUE backend-issued token — never a provider key.
 *
 * Today it returns `unavailable` because the backend endpoint is not yet shipped
 * (see the BACKEND GAP note above). The signature is the real one so call sites
 * are already correct: when the endpoint lands, swap the body for the real POST.
 *
 * @param _client  Typed api client (auth-scoped). Unused until the endpoint
 *                 exists; kept in the signature so the seam is wired.
 * @param _callId  Canonical call UUID to issue a room token for.
 */
export async function fetchHandoffRoomToken(
  _client: ApiClient,
  _callId: string,
): Promise<HandoffTokenResult> {
  // REAL IMPLEMENTATION (pending backend POST /api/v1/livekit/token):
  //   const result = await _client.POST('/api/v1/livekit/token', {
  //     body: { callId: _callId }, parseAs: 'json',
  //   });
  //   if (result.error) {
  //     const err = toApiError(result.error, result.response?.status ?? 0);
  //     return { status: 'error', reason: err.isForbidden ? 'token_denied' : 'failed' };
  //   }
  //   return { status: 'ready', token: handoffTokenSchema.parse(result.data) };
  // The token is passed straight to useHandoffRoom and NEVER stored.
  return { status: 'error', reason: 'unavailable' };
}

/** Signature of the token-fetch seam — overridable in tests / mock harnesses. */
export type HandoffTokenFetcher = (
  client: ApiClient,
  callId: string,
) => Promise<HandoffTokenResult>;

/**
 * Fetch a handoff room token for `callId` after a permitted handoff.
 *
 * Pass `callId = null` (flag off, permission absent, or call not yet in a
 * handoff state) to stay idle and make NO request. The ready token is fed
 * directly to `useHandoffRoom`; it lives only in component state and is
 * deliberately NOT placed in the react-query cache.
 */
export function useHandoffRoomToken(
  callId: string | null,
  fetcher: HandoffTokenFetcher = fetchHandoffRoomToken,
): HandoffTokenResult {
  const client = useApiClient();
  const [result, setResult] = useState<HandoffTokenResult>({ status: 'idle' });

  // Latest fetcher in a ref so an unstable reference does not retrigger.
  const fetcherRef = useRef<HandoffTokenFetcher>(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!callId) {
      setResult({ status: 'idle' });
      return;
    }

    let active = true;
    setResult({ status: 'loading' });

    fetcherRef
      .current(client, callId)
      .then((next) => {
        if (active) setResult(next);
      })
      .catch((err: unknown) => {
        if (!active) return;
        const reason: HandoffTokenErrorReason =
          err instanceof ApiError && err.isForbidden ? 'token_denied' : 'failed';
        setResult({ status: 'error', reason });
      });

    return () => {
      active = false;
    };
  }, [client, callId]);

  return result;
}
